import MidiParser from 'midi-parser-js';
import {PROCESSOR_COUNT} from "../MidiPlay";

import Colors from '../resource/ResourceColors.jsx';
import {ASSERT} from "../util/UtilDebug.jsx";

export function clamp(num, min, max) {
    return num <= min ? min : num >= max ? max : num;
}

const s3_root = 'https://mikehallstudio.s3.amazonaws.com/tone-gallery/instruments/GrandB/';

const MIN_DECAY = 500;
const MAX_DECAY = 1000;
const DECAY_RANGE = MAX_DECAY - MIN_DECAY;

export class UtilMidi {

    static ticks_keyname(ticks) {
        let ticks_str = ticks.toString();
        const padding = 12 - ticks_str.length;
        for (let i = 0; i < padding; i++) {
            ticks_str = '0' + ticks_str;
        }
        return `tick_${ticks_str}`;
    }

    static note_off(in_sustain, sustain_release, actions, data, track) {
        const action = {type: 8, data: data, track: track};
        if (in_sustain) {
            sustain_release.push(action);
        } else {
            actions.push(action);
        }
    }

    static note_on(in_sustain, sustain_release, actions, data, track) {
        const action = {type: 9, data: data, track: track};
        if (in_sustain) {
            for (let i = 0; i < sustain_release.length; i++) {
                if (sustain_release[i].data[0] === data[0]) {
                    // console.log('found duplicate note in sustain', data);
                    sustain_release.splice(i, 1);
                    const note_off_action = {type: 8, data: data};
                    actions.push(note_off_action);
                    break;
                }
            }
        }
        actions.push(action);
    }

    static get_volume(audio_index, velocity) {
        switch (audio_index) {
            case 0:
                return velocity / 128.0;
            case 1:
                return velocity / 128.0;
            case 2:
                return velocity / 144.0;
            case 3:
                return velocity / 256.0;
            case 4:
                return velocity / 256.0;
            default:
                return velocity / 256.0;
        }
    }

    static pad(num, size) {
        num = num.toString();
        while (num.length < size) num = "0" + num;
        return num;
    }

    static prepare_events(data) {
        let tick_events = {};
        let sustain_release = [];
        let in_sustain = false;
        for (let i = 0; i < data.track.length; i++) {
            let tick_index = 0;
            let sustain_index = 0;
            let sustain_count = 1;
            const track = data.track[i];

            for (let j = 0; j < track.event.length; j++) {

                const event = track.event[j];
                tick_index += event.deltaTime;
                let tick_keyname = UtilMidi.ticks_keyname(tick_index);
                if (!tick_events[tick_keyname]) {
                    tick_events[tick_keyname] = {
                        tick_index: tick_index,
                        actions: []
                    }
                }

                switch (event.type) {
                    case 8: { // note off
                        UtilMidi.note_off(
                            in_sustain,
                            sustain_release,
                            tick_events[tick_keyname].actions,
                            event.data,
                            i);
                        break;
                    }

                    case 9: { // note on
                        const event_type = (event.data.length > 1 && event.data[1] === 0) ? 8 : 9;
                        if (event_type === 8) {
                            UtilMidi.note_off(
                                in_sustain,
                                sustain_release,
                                tick_events[tick_keyname].actions,
                                event.data,
                                i);
                        } else {
                            UtilMidi.note_on(
                                in_sustain,
                                sustain_release,
                                tick_events[tick_keyname].actions,
                                event.data,
                                i);
                        }
                        break;
                    }

                    case 12: { // program change
                        break;
                    }

                    case 11: { // controller
                        const controller = event.data[0];
                        switch (controller) {
                            case 64:
                                in_sustain = event.data[1] > 64 ? true : false;
                                if (!in_sustain) {
                                    sustain_index = sustain_count++;
                                    sustain_release.forEach(action => {
                                        tick_events[tick_keyname].actions.push(action);
                                    });
                                    sustain_release = [];
                                } else {
                                    sustain_index = 0;
                                }
                                break;
                            default:
                                console.log("unknown controller #" + event.data[0] + ", " + event.data[1]);
                                break;
                        }
                        break;
                    }

                    case 255: // meta events
                        switch (event.metaType) {
                            case 81 : // tempo change
                                tick_events[tick_keyname].actions.push({
                                    type: 255,
                                    metaType: 81,
                                    data: event.data
                                });
                                break;
                            case 84 : // SMPTE Offset
                                break;
                            case 88 : // time signature
                                tick_events[tick_keyname].actions.push({
                                    type: 255,
                                    metaType: 88,
                                    data: event.data
                                });
                                break;
                            case 89 : // key signature
                                tick_events[tick_keyname].actions.push({
                                    type: 255,
                                    metaType: 89,
                                    data: event.data
                                });
                                break;
                            case 47 : // end of track
                                break;
                            default:
                                console.log("unhandled meta event on track " + i + ", " + event.metaType, event);
                                break;
                        }
                        break

                    default:
                        console.log("unhandled event", event);
                        break;
                }

                tick_events[tick_keyname].sustain_index = sustain_index;
            }
        }

        // set the absolute timing and processor assignments
        let processor_events = [];
        for (let p_index = 0; p_index < PROCESSOR_COUNT; p_index++) {
            processor_events.push([]);
        }
        const sorted_keys = Object.keys(tick_events).sort();
        let midi_clock = 500000;
        let new_midi_clock = 500000;

        let proccessor_queue = [];
        for (let p_index = 0; p_index < PROCESSOR_COUNT; p_index++) {
            proccessor_queue.push(p_index);
        }
        let processor_stacks = {};
        let timecode = 0;
        let prev_timecode = 0;
        let tick_index = 0;
        let prev_tick_index = 0;

        let time_signature = {
            numerator: 4,
            denominator: 4,
            ticks_per_quarter: 24
        };

        let event_index = 1;
        const time_events = sorted_keys.map(key => {
            if (!tick_events.hasOwnProperty(key)) {
                return null;
            }
            const event = tick_events[key];
            let processor;
            // event.actions.sort((a1, a2) => a1.type - a2.type);
            event.actions.forEach(action => {
                const tone_name = 'tone_' + action.data[0];
                switch (action.type) {

                    case 8: // note off
                        if (processor_stacks[tone_name] === undefined) {
                            console.error('tone_name processor not found', tone_name);
                            break;
                        }
                        processor = processor_stacks[tone_name].shift();
                        ASSERT(processor !== undefined, 'processor is undefined');
                        action.processor = processor;
                        proccessor_queue.push(processor);
                        break;

                    case 9: // note on
                        ASSERT(proccessor_queue.length, 'processor queue is empty');
                        if (!proccessor_queue.length) {
                            break;
                        }
                        processor = proccessor_queue.shift();
                        ASSERT(processor !== undefined, 'processor is undefined');
                        if (processor === undefined) {
                            break;
                        }
                        if (!processor_stacks[tone_name]) {
                            processor_stacks[tone_name] = [];
                        }
                        processor_stacks[tone_name].push(processor);
                        processor_events[processor].push(action);
                        action.processor = processor;
                        break;

                    case 255: // meta events
                        switch (action.metaType) {
                            case 81 : // tempo change
                                new_midi_clock = action.data;
                                break;
                            case 88 : // time signature change
                                time_signature = {
                                    numerator: action.data[0],
                                    denominator: Math.pow(2, action.data[1]),
                                    ticks_per_quarter: action.data[2],
                                }
                                event.time_signature = time_signature;
                                break;
                            default:
                                console.log('unknown action.metaType', action)
                                break;
                        }
                        break;
                    default:
                        console.log('unknown action.type', action)
                        break;
                }
            });
            prev_tick_index = tick_index;
            tick_index = event.tick_index;
            prev_timecode = timecode;
            timecode = prev_timecode + (tick_index - prev_tick_index) * midi_clock / (1000 * data.timeDivision);
            event.timecode = timecode
            event.index = event_index++;
            midi_clock = new_midi_clock;
            event.midi_clock = new_midi_clock;
            return event;
        });

        time_events.forEach((event, index) => {
            const note_ons = event.actions.filter(a => a.type === 9);
            event.duration_sets = {};
            note_ons.forEach(note_on => {

                const tone = note_on.data[0];
                const velocity = note_on.data[1];

                const audio_index = Math.floor(velocity / 40);
                note_on.volume = UtilMidi.get_volume(audio_index, velocity);
                note_on.audio_index = audio_index;

                const position = (tone - 20) / 88.0;
                note_on.volume_left = note_on.volume / 4 + note_on.volume * (1.0 - position) / 3;
                note_on.volume_right = note_on.volume / 4 + note_on.volume * position / 3;

                note_on.hsl = Colors.note_to_hsl(tone, 0.85);
                note_on.hsl_str = 'hsl(' + note_on.hsl.h + ',' + note_on.hsl.s + '%,' + note_on.hsl.l + '%)';

                let loudness = 'p';
                switch (audio_index) {
                    default:
                    case 0:
                        loudness = 'p';
                        break;
                    case 1:
                        loudness = 'mp';
                        break;
                    case 2:
                        loudness = 'mf';
                        break;
                    case 3:
                        loudness = 'f';
                        break;
                    case 4:
                        loudness = 'ff';
                        break;
                }
                const three_digit_tone = UtilMidi.pad(tone, 3);
                note_on.audio_src = s3_root + loudness + '/GrandB_' + loudness + '_' + three_digit_tone + '_mono.mp3';

                let found_it = [];
                for (let event_index = index + 1; event_index < time_events.length; event_index++) {
                    found_it = time_events[event_index].actions
                        .filter(a => a.type === 8)
                        .filter(a => a.data[0] === tone);
                    if (!found_it.length) {
                        continue;
                    }
                    const duration_ms = Math.floor(time_events[event_index].timecode - event.timecode);
                    note_on.duration_ms = duration_ms;

                    const decay_left = MIN_DECAY + note_on.volume_left * DECAY_RANGE;
                    const decay_right = MIN_DECAY + note_on.volume_right * DECAY_RANGE;

                    const decay_backoff = Math.max(decay_left, decay_right) / 2;
                    note_on.decay_begin = clamp(duration_ms - decay_backoff, 25, 9000);

                    note_on.decay_left_seconds = decay_left / 1000.0;
                    note_on.decay_right_seconds = decay_right / 1000.0;
                    note_on.decay = Math.max(decay_left, decay_right);
                    break;
                }
                if (!found_it.length) {
                    ASSERT(false, 'could not find note off for note', note_on);
                }
            });
        });

        return {
            midi_format: data.formatType,
            time_division: data.timeDivision,
            track_count: data.track.length,
            time_events: time_events,
            processor_events: processor_events
        };
    }

    static events_from_midi(blob) {
        const midi = new Uint8Array(blob);
        const data = MidiParser.parse(midi);
        console.log('parsed midi', data);
        return UtilMidi.prepare_events(data);
    }
}

export default UtilMidi;